import { api, graphql } from './client';
import { getSession } from './session';
import type {
  LlxResponse,
  LoginRequest,
  LoginSession,
  LoginDetailsRequest,
  LoginDetails,
  InventoryPackage,
  TransactionItem,
  TransactionsRequest,
  PaginatedData,
  SalesSummaryRequest,
  Brand,
  JsonApiListResponse,
  JsonApiSingleResponse,
  PurchaseOrderSummary,
  PurchaseOrderDetail,
  PurchaseOrderListParams,
  PurchaseOrderUpsertRequest,
} from './types';

export const login = async (json: LoginRequest) =>
  api.post('api/posv3/user/EmployeeLogin', { json }).json<LlxResponse<LoginSession>>();

export const getLoginDetails = async (json: LoginDetailsRequest) =>
  api.post('api/posv3/user/EmployeeLoginDetails', { json }).json<LlxResponse<LoginDetails>>();

// ─── Inventory (GraphQL) ─────────────────────

const GET_PACKAGES = /* GraphQL */ `
  query getPackages(
    $lspId: Int!
    $locId: Int!
    $userId: Int!
    $whereClause: PackageFilterInput!
    $take: Int
  ) {
    packages(where: $whereClause, take: $take, locId: $locId, lspId: $lspId, userId: $userId) {
      items {
        id: packageId
        quantity
        receivedDate
        expirationDate
        product {
          id
          whseProductsDescription
          whseProductsProductNo
          unitCost
          thccontent
          cbdcontent
          isRetired
          lowInventoryThreshold
          productTypeNavigation {
            productType
          }
          brand {
            brandId
            brandName
            brandCatalogBrandId
          }
        }
        vendor {
          id
          vendorName
        }
        unit {
          unitId
          abbreviation
        }
      }
    }
  }
`;

type PackagesResponse = {
  packages: { items: InventoryPackage[] };
};

export const getInventoryPackages = async (
  whereClause: Record<string, unknown> = {},
): Promise<InventoryPackage[]> => {
  const s = getSession();
  const data = await graphql<PackagesResponse>(GET_PACKAGES, {
    lspId: s?.LspId ?? 0,
    locId: s?.LocId ?? 0,
    userId: s?.UserId ?? 0,
    whereClause,
    take: 10000,
  });
  return data.packages.items;
};

export const getJwt = async () => api.post('api/jwt', { json: {} }).text();

// ─── Transactions ─────────────────────────────

export const getTransactions = async (json: TransactionsRequest) =>
  api
    .post('api/v2/transactions/list', { json })
    .json<LlxResponse<PaginatedData<TransactionItem>>>();

// ─── Sales Reports ────────────────────────────

export const getSalesSummary = async (json: SalesSummaryRequest) =>
  api
    .post('api/posv3/financial-reports/v1/sales-summary-overview', { json })
    .json<LlxResponse<unknown>>();

// ─── Brands (GraphQL) ─────────────────────────

const GET_BRANDS = /* GraphQL */ `
  query getBrands($lspId: Int!) {
    brands(lspId: $lspId) {
      items {
        brandId
        brandName
        brandCatalogBrandId
        isDeleted
      }
    }
  }
`;

type BrandsResponse = {
  brands: { items: Brand[] };
};

export const getBrands = async (): Promise<Brand[]> => {
  const s = getSession();
  const data = await graphql<BrandsResponse>(GET_BRANDS, {
    lspId: s?.LspId ?? 0,
  });
  return data.brands.items;
};

// ─── Purchase Orders ──────────────────────────

export const listPurchaseOrders = async (params: PurchaseOrderListParams = {}) =>
  api
    .post('api/v2/purchase-orders/list', { json: params })
    .json<JsonApiListResponse<PurchaseOrderSummary>>();

export const getPurchaseOrder = async (id: string) =>
  api
    .post(`api/v2/purchase-orders/${id}`, { json: {} })
    .json<JsonApiSingleResponse<PurchaseOrderDetail>>();

export const upsertPurchaseOrder = async (body: PurchaseOrderUpsertRequest) =>
  api
    .post('api/v2/purchase-orders/upsert', { json: body })
    .json<JsonApiSingleResponse<PurchaseOrderDetail>>();
